import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Chip,
  Grid,
  Link as MuiLink,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink } from 'react-router-dom';
import {
  plural,
  summaryLine,
  type ColumnMapping,
  type ImportGuide,
  type ImportKindInfo,
  type ImportOutcome,
  type ImportResult,
  type ImportRow
} from './dataImportApi.ts';

const OUTCOMES: Record<
  ImportOutcome,
  { label: string; color: 'success' | 'info' | 'default' | 'warning' | 'error' }
> = {
  create: { label: 'Add', color: 'success' },
  update: { label: 'Fill in', color: 'info' },
  unchanged: { label: 'In Forge', color: 'default' },
  duplicate: { label: 'Repeat', color: 'warning' },
  error: { label: 'Skipped', color: 'error' }
};

// A big file previews its first rows; the counts above cover all of them.
const MAX_SHOWN = 500;

type Kind = ImportKindInfo;

const needsALook = (row: ImportRow) =>
  row.outcome === 'error' || row.outcome === 'duplicate' || row.warnings.length > 0;

const RowNote = ({ row, kind }: { row: ImportRow; kind: Kind }) => {
  const lines: { text: string; error?: boolean }[] = [];
  if (row.error) lines.push({ text: row.error, error: true });
  if (row.fills.length) lines.push({ text: `Fills in ${row.fills.join(', ')}.` });
  for (const warning of row.warnings) lines.push({ text: warning });
  return (
    <>
      {row.match ? (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
          Matches{' '}
          <MuiLink
            component={RouterLink}
            to={`${kind.listPath}/${row.match.id}/show`}
            target="_blank"
            rel="noopener"
          >
            {row.match.label}
          </MuiLink>
        </Typography>
      ) : null}
      {lines.map((line) => (
        <Typography
          key={line.text}
          variant="caption"
          color={line.error ? 'error' : 'text.secondary'}
          sx={{ display: 'block', mt: 0.5, maxWidth: 360 }}
        >
          {line.text}
        </Typography>
      ))}
    </>
  );
};

type ImportPreviewProps = {
  kind: Kind;
  guide: ImportGuide;
  preview: ImportResult;
  mapping: ColumnMapping;
  busy: boolean;
  onColumn: (field: string, header: string) => void;
  onValue: (field: string, value: string, optionId: string) => void;
};

// What the file would do, before anything is written: its columns, the values
// Forge has to be told about, and each row's outcome with the reason.
export const ImportPreview = ({
  kind,
  guide,
  preview,
  mapping,
  busy,
  onColumn,
  onValue
}: ImportPreviewProps) => {
  const [filter, setFilter] = useState<'all' | 'look'>('all');
  const matched = guide.fields.filter((field) => mapping[field.key]).length;
  const rows = filter === 'all' ? preview.rows : preview.rows.filter(needsALook);
  const headers = preview.headers.filter(Boolean);

  return (
    <Stack spacing={2}>
      {preview.missing ? <Alert severity="info">{preview.missing}</Alert> : null}

      <Accordion
        disableGutters
        elevation={0}
        defaultExpanded={Boolean(preview.missing)}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          '&::before': { display: 'none' }
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">
            Columns: {matched} of {guide.fields.length} matched
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={1.5}>
            {guide.fields.map((field) => (
              <Grid key={field.key} size={{ xs: 12, sm: 6, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  label={field.label}
                  value={mapping[field.key] ?? ''}
                  onChange={(event) => onColumn(field.key, event.target.value)}
                  disabled={busy}
                >
                  <MenuItem value="">
                    <em>Not in this file</em>
                  </MenuItem>
                  {headers.map((header) => (
                    <MenuItem key={header} value={header}>
                      {header}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            ))}
          </Grid>
        </AccordionDetails>
      </Accordion>

      {preview.choices.map((choice) => (
        <Box key={choice.field} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Typography variant="subtitle2">{choice.label} values Forge doesn&apos;t have</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Say which of yours each one means.
          </Typography>
          <Stack spacing={1}>
            {choice.values.map((entry) => (
              <Box
                key={entry.value}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 260px' },
                  gap: 1,
                  alignItems: 'center'
                }}
              >
                <Typography variant="body2">
                  {entry.value}{' '}
                  <Typography component="span" variant="caption" color="text.secondary">
                    ({plural(entry.rows, 'row', 'rows')})
                  </Typography>
                </Typography>
                <TextField
                  select
                  size="small"
                  value={entry.chosen ?? ''}
                  onChange={(event) => onValue(choice.field, entry.value, event.target.value)}
                  disabled={busy}
                  slotProps={{
                    select: { displayEmpty: true },
                    htmlInput: { 'aria-label': `${choice.label} for ${entry.value}` }
                  }}
                >
                  <MenuItem value="">
                    <em>{choice.fallback}</em>
                  </MenuItem>
                  {choice.options.map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}

      {preview.warnings.map((warning) => (
        <Alert key={warning} severity="info">
          {warning}
        </Alert>
      ))}

      {preview.rows.length ? (
        <>
          <Box>
            <Typography variant="body2">{summaryLine(preview, kind)}</Typography>
            <Typography variant="caption" color="text.secondary">
              {kind.kind === 'opportunities'
                ? 'Opportunities are always added. One the contact already has, the same in every column, is left alone.'
                : `${kind.label} already in Forge keep what they have; the file only fills in blanks.`}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filter}
              onChange={(_event, value: 'all' | 'look' | null) => value && setFilter(value)}
              aria-label="Which rows"
            >
              <ToggleButton value="all" sx={{ px: 1.5 }}>
                Every row
              </ToggleButton>
              <ToggleButton value="look" sx={{ px: 1.5 }}>
                Needs a look ({preview.rows.filter(needsALook).length.toLocaleString()})
              </ToggleButton>
            </ToggleButtonGroup>
            {rows.length > MAX_SHOWN ? (
              <Typography variant="caption" color="text.secondary">
                Showing the first {MAX_SHOWN.toLocaleString()} of {rows.length.toLocaleString()}.
              </Typography>
            ) : null}
          </Box>

          <TableContainer
            sx={{ maxHeight: 420, border: 1, borderColor: 'divider', borderRadius: 1 }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Line</TableCell>
                  <TableCell>{kind.rowLabel}</TableCell>
                  <TableCell>Result</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.slice(0, MAX_SHOWN).map((row) => (
                  <TableRow key={row.line}>
                    <TableCell sx={{ color: 'text.secondary', verticalAlign: 'top' }}>
                      {row.line}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top', overflowWrap: 'anywhere' }}>
                      <Typography variant="body2">{row.label}</Typography>
                      {row.detail ? (
                        <Typography variant="caption" color="text.secondary">
                          {row.detail}
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell sx={{ verticalAlign: 'top' }}>
                      <Chip
                        size="small"
                        label={OUTCOMES[row.outcome].label}
                        color={OUTCOMES[row.outcome].color}
                      />
                      <RowNote row={row} kind={kind} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : !preview.missing ? (
        <Alert severity="info">The file has no rows under its header.</Alert>
      ) : null}
    </Stack>
  );
};
